import { CreatableComboboxField } from "@repo/ui/combobox/creatableComboboxField";
import type { FC } from "react";
import { applyAllowedDomainsChange } from "../formConfigs/webDocumentResearchAllowedDomains.js";
import type { BaseToolFormData } from "../hooks/useBaseToolForm.js";
import { SharedToolFormFields } from "./sharedToolFormFields.js";

interface WebDocumentResearchToolFormProps {
  name: string;
  description: string;
  settings: WebDocumentResearchToolFormSettings;
  onNameChange: (name: string) => void;
  onDescriptionChange: (description: string) => void;
  onSettingsChange: (
    settings: Partial<WebDocumentResearchToolFormSettings>,
  ) => void;
  /** The saved tool, to diff against; `undefined` for a new tool. */
  original: BaseToolFormData<WebDocumentResearchToolFormSettings> | undefined;
  /** Set while a save is in flight: the editors take no edits. */
  isReadOnly: boolean;
  /** Shows the fields' changes. When set, their toggles don't render. */
  isShowingChanges?: boolean;
}

export interface WebDocumentResearchToolFormSettings {
  allowedDomains: readonly string[];
  allowedDomainsError: string | undefined;
}

export const WebDocumentResearchToolForm: FC<
  WebDocumentResearchToolFormProps
> = (props) => {
  const {
    name,
    description,
    settings,
    onNameChange,
    onDescriptionChange,
    onSettingsChange,
    original,
    isReadOnly,
    isShowingChanges,
  } = props;

  return (
    <div className="grid grid-cols-2 gap-4">
      <SharedToolFormFields
        name={name}
        description={description}
        onNameChange={onNameChange}
        originalName={original?.name}
        originalDescription={original?.description}
        onDescriptionChange={onDescriptionChange}
        isReadOnly={isReadOnly}
        isShowingChanges={isShowingChanges}
        namePlaceholder="Research web documents"
        descriptionPlaceholder="Look up the answer to a question or research query by searching a set of documents given their URLs."
      />

      <div className="col-span-full">
        <CreatableComboboxField
          label="Allowed domains"
          placeholder="Add a domain (e.g. documents.example.com)"
          value={settings.allowedDomains}
          onChange={(allowedDomains) =>
            onSettingsChange(
              applyAllowedDomainsChange(
                settings.allowedDomains,
                allowedDomains,
              ),
            )
          }
          description="Document URLs passed to the tool to perform research on must match one of these domains. Enter exact hostnames without https://, a path, port, or wildcard. Subdomains must be added separately."
          errorMessage={settings.allowedDomainsError}
        />
      </div>
    </div>
  );
};
